import { FC, useCallback, useContext, useEffect } from "react";
import cn from "classnames";
import { Icon, IconName } from "../Icon";
import * as Color from "../Color";
import styles from "./Alert.module.scss";
import { AlertType } from "./AlertType";
import { AlertStyle } from "./AlertStyle";
import { AlertContext } from "./AlertContext";
import { Button, CloseButtonAppearance } from "../Button";

/**
 * The alert component props
 */
export interface AlertProps {
  /**
   * The alert id.
   */
  readonly id?: string;

  /**
   * The optional test ID.
   */
  readonly testId?: string;

  /**
   * The alert CSS class name.
   */
  readonly className?: string;

  /**
   * The alert message
   */
  readonly message?: string | Error;

  /**
   * The type of the alert component.
   */
  readonly type?: AlertType;

  /**
   * The style of the alert component.
   */
  readonly style?: AlertStyle;

  /** Indicates whether the alert is on the root layer. */
  readonly root: boolean;

  /**
   * Whether the alert is critical and should be announced immediately
   */
  readonly critical?: boolean;
}

/**
 * The mapping of alert types to icons.
 */
type AlertIcons = Record<AlertType, IconName>;

/**
 * The alert component
 */
export const Alert: FC<AlertProps> = (props: AlertProps): JSX.Element => {
  const { testId, className: auxClassName, type = "success", style = "banner", message, root, critical = false } = props;

  const { hideAlert } = useContext(AlertContext);

  const handleRemoveClick = useCallback(() => {
    hideAlert();
  }, [hideAlert]);

  useEffect(() => {
    const isValidType = message instanceof Error || typeof message === "string";
    if (!isValidType) {
      console.warn("An attempt was made to pass invalid data into an alert message.");
      console.warn("Only Error instances and strings are supported, but got", message);
    }
  }, [message]);

  const alertClasses = cn("alert", styles.Alert, styles[type], styles[style], auxClassName);

  const alertIcons: AlertIcons = {
    success: "check-circle",
    warning: "warning-circle",
    error: "x-circle",
    informational: "info-circle",
  };

  const removeIcon = (
    // TODO: add key handlers to buttons
    <Button
      icon="x"
      appearance={CloseButtonAppearance}
      aria-label="Close alert"
      tabIndex={0}
      onClick={handleRemoveClick}
      className={styles.removeIcon}/>
  );

  let messageText = "An unknown error occurred.";
  if (message instanceof Error) {
    messageText = message.message;
  } else if (typeof message === "string") {
    messageText = message;
  }
  // TODO: revisit focus management for alerts
  return (
    <div role="alert" aria-live={critical ? "assertive" : "polite"} data-testid={(testId && root) ? `test__${testId}` : undefined} className={alertClasses}>
      {style === "banner" && <div role="presentation" className={styles.pseudo} />}
      <div className={styles.messageContainer}>
        <Icon aria-label={`${type} icon`} className={styles.alertIcon} name={alertIcons[type]} color={Color.White} size="regular" />
        <span className="message">{messageText}</span>
      </div>
      {style === "banner" && removeIcon}
    </div>
  );
};
